const GOOGLE_PAY_POPUP_RECOVERY_POLICY = {
  closedPollIntervalMs: 250,
  providerSettlementGraceMs: 3_500,
} as const;

export type ProviderPopupHost = {
  open: Window['open'];
  setTimeout(handler: TimerHandler, timeout?: number): number;
  clearTimeout(timeoutId: number): void;
  document?: Pick<Document, 'hasFocus'>;
};

export type ProviderPopupRecovery<T> = {
  result: T;
  stop(): void;
};

/**
 * Captures the popup synchronously opened by a provider launch and supplies a
 * bounded fallback when the provider leaves its result promise pending after
 * the popup has actually closed.
 */
export const launchWithProviderPopupRecovery = <T>(
  launch: () => T,
  onAbandoned: () => void,
  host: ProviderPopupHost = window,
): ProviderPopupRecovery<T> => {
  const originalOpen = host.open;
  let capturedPopup: Window | null = null;
  let captureInstalled = false;
  let stopped = false;
  let pollTimer: number | null = null;
  let settlementTimer: number | null = null;

  const captureOpen = ((url?: string | URL, target?: string, features?: string) => {
    const popup = originalOpen.call(host, url, target, features);
    if (!capturedPopup && popup) capturedPopup = popup;
    return popup;
  }) as Window['open'];

  try {
    host.open = captureOpen;
    captureInstalled = host.open === captureOpen;
  } catch {
    captureInstalled = false;
  }

  let result: T;
  try {
    result = launch();
  } finally {
    if (captureInstalled && host.open === captureOpen) host.open = originalOpen;
  }

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (pollTimer !== null) host.clearTimeout(pollTimer);
    if (settlementTimer !== null) host.clearTimeout(settlementTimer);
    pollTimer = null;
    settlementTimer = null;
    capturedPopup = null;
  };

  const merchantDocumentHasFocus = (): boolean => {
    try {
      return host.document?.hasFocus() ?? true;
    } catch {
      return false;
    }
  };

  const poll = (): void => {
    if (stopped || !capturedPopup) return;
    let closed = false;
    try {
      closed = capturedPopup.closed;
    } catch {
      stop();
      return;
    }
    // Chrome can close Google's short-lived launcher window after handing the
    // flow to its native payment-handler window. The launcher closing is only
    // a customer dismissal once focus has returned to the merchant document.
    if (!closed || !merchantDocumentHasFocus()) {
      pollTimer = host.setTimeout(poll, GOOGLE_PAY_POPUP_RECOVERY_POLICY.closedPollIntervalMs);
      return;
    }
    pollTimer = null;
    settlementTimer = host.setTimeout(() => {
      if (stopped) return;
      settlementTimer = null;
      if (!merchantDocumentHasFocus()) {
        pollTimer = host.setTimeout(poll, GOOGLE_PAY_POPUP_RECOVERY_POLICY.closedPollIntervalMs);
        return;
      }
      stop();
      onAbandoned();
    }, GOOGLE_PAY_POPUP_RECOVERY_POLICY.providerSettlementGraceMs);
  };

  if (capturedPopup) {
    pollTimer = host.setTimeout(poll, GOOGLE_PAY_POPUP_RECOVERY_POLICY.closedPollIntervalMs);
  }

  return { result, stop };
};
